import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/** Only allow https: URLs for untrusted image sources (e.g. Nostr profile pictures). */
export function safeImageUrl(url: string | undefined): string | undefined {
  if (!url) return undefined;
  try {
    return new URL(url).protocol === 'https:' ? url : undefined;
  } catch {
    return undefined;
  }
}

// CSS named colors with their RGB values
const CSS_COLORS: [number, number, number, string][] = [
  [255, 255, 255, "White"],
  [0, 0, 0, "Black"],
  [255, 0, 0, "Red"],
  [0, 128, 0, "Green"],
  [0, 0, 255, "Blue"],
  [255, 255, 0, "Yellow"],
  [0, 255, 255, "Cyan"],
  [255, 0, 255, "Magenta"],
  [192, 192, 192, "Silver"],
  [128, 128, 128, "Gray"],
  [128, 0, 0, "Maroon"],
  [128, 128, 0, "Olive"],
  [0, 128, 128, "Teal"],
  [0, 0, 128, "Navy"],
  [255, 165, 0, "Orange"],
  [255, 192, 203, "Pink"],
  [128, 0, 128, "Purple"],
  [165, 42, 42, "Brown"],
  [255, 127, 80, "Coral"],
  [220, 20, 60, "Crimson"],
  [0, 255, 0, "Lime"],
  [75, 0, 130, "Indigo"],
  [238, 130, 238, "Violet"],
  [64, 224, 208, "Turquoise"],
  [255, 215, 0, "Gold"],
  [240, 230, 140, "Khaki"],
  [230, 230, 250, "Lavender"],
  [255, 228, 196, "Bisque"],
  [245, 245, 220, "Beige"],
  [255, 248, 220, "Cornsilk"],
  [255, 250, 205, "Lemon Chiffon"],
  [250, 250, 210, "Light Goldenrod"],
  [255, 255, 224, "Light Yellow"],
  [255, 250, 240, "Floral White"],
  [255, 245, 238, "Seashell"],
  [255, 240, 245, "Lavender Blush"],
  [255, 228, 225, "Misty Rose"],
  [255, 235, 205, "Blanched Almond"],
  [255, 239, 213, "Papaya Whip"],
  [255, 218, 185, "Peach Puff"],
  [255, 222, 173, "Navajo White"],
  [255, 228, 181, "Moccasin"],
  [245, 222, 179, "Wheat"],
  [222, 184, 135, "Burlywood"],
  [210, 180, 140, "Tan"],
  [188, 143, 143, "Rosy Brown"],
  [244, 164, 96, "Sandy Brown"],
  [218, 165, 32, "Goldenrod"],
  [184, 134, 11, "Dark Goldenrod"],
  [205, 133, 63, "Peru"],
  [210, 105, 30, "Chocolate"],
  [139, 69, 19, "Saddle Brown"],
  [160, 82, 45, "Sienna"],
  [255, 99, 71, "Tomato"],
  [255, 69, 0, "Orange Red"],
  [255, 140, 0, "Dark Orange"],
  [255, 160, 122, "Light Salmon"],
  [250, 128, 114, "Salmon"],
  [233, 150, 122, "Dark Salmon"],
  [240, 128, 128, "Light Coral"],
  [205, 92, 92, "Indian Red"],
  [178, 34, 34, "Firebrick"],
  [139, 0, 0, "Dark Red"],
  [255, 105, 180, "Hot Pink"],
  [255, 20, 147, "Deep Pink"],
  [199, 21, 133, "Medium Violet Red"],
  [219, 112, 147, "Pale Violet Red"],
  [255, 182, 193, "Light Pink"],
  [216, 191, 216, "Thistle"],
  [221, 160, 221, "Plum"],
  [218, 112, 214, "Orchid"],
  [186, 85, 211, "Medium Orchid"],
  [148, 0, 211, "Dark Violet"],
  [153, 50, 204, "Dark Orchid"],
  [138, 43, 226, "Blue Violet"],
  [147, 112, 219, "Medium Purple"],
  [106, 90, 205, "Slate Blue"],
  [123, 104, 238, "Medium Slate Blue"],
  [72, 61, 139, "Dark Slate Blue"],
  [102, 51, 153, "Rebecca Purple"],
  [176, 224, 230, "Powder Blue"],
  [173, 216, 230, "Light Blue"],
  [135, 206, 250, "Light Sky Blue"],
  [135, 206, 235, "Sky Blue"],
  [0, 191, 255, "Deep Sky Blue"],
  [30, 144, 255, "Dodger Blue"],
  [100, 149, 237, "Cornflower Blue"],
  [70, 130, 180, "Steel Blue"],
  [65, 105, 225, "Royal Blue"],
  [0, 0, 205, "Medium Blue"],
  [0, 0, 139, "Dark Blue"],
  [25, 25, 112, "Midnight Blue"],
  [176, 196, 222, "Light Steel Blue"],
  [119, 136, 153, "Light Slate Gray"],
  [112, 128, 144, "Slate Gray"],
  [47, 79, 79, "Dark Slate Gray"],
  [224, 255, 255, "Light Cyan"],
  [175, 238, 238, "Pale Turquoise"],
  [127, 255, 212, "Aquamarine"],
  [0, 206, 209, "Dark Turquoise"],
  [95, 158, 160, "Cadet Blue"],
  [32, 178, 170, "Light Sea Green"],
  [0, 139, 139, "Dark Cyan"],
  [144, 238, 144, "Light Green"],
  [152, 251, 152, "Pale Green"],
  [0, 255, 127, "Spring Green"],
  [0, 250, 154, "Medium Spring Green"],
  [50, 205, 50, "Lime Green"],
  [60, 179, 113, "Medium Sea Green"],
  [46, 139, 87, "Sea Green"],
  [34, 139, 34, "Forest Green"],
  [0, 100, 0, "Dark Green"],
  [107, 142, 35, "Olive Drab"],
  [85, 107, 47, "Dark Olive Green"],
  [143, 188, 143, "Dark Sea Green"],
  [189, 183, 107, "Dark Khaki"],
  [154, 205, 50, "Yellow Green"],
  [124, 252, 0, "Lawn Green"],
  [127, 255, 0, "Chartreuse"],
  [173, 255, 47, "Green Yellow"],
  [102, 205, 170, "Medium Aquamarine"],
  [72, 209, 204, "Medium Turquoise"],
  [245, 255, 250, "Mint Cream"],
  [240, 255, 240, "Honeydew"],
  [240, 255, 255, "Azure"],
  [240, 248, 255, "Alice Blue"],
  [248, 248, 255, "Ghost White"],
  [255, 250, 250, "Snow"],
  [105, 105, 105, "Dim Gray"],
  [169, 169, 169, "Dark Gray"],
  [211, 211, 211, "Light Gray"],
  [220, 220, 220, "Gainsboro"],
  [245, 245, 245, "White Smoke"],
  [250, 240, 230, "Linen"],
  [253, 245, 230, "Old Lace"],
  [250, 235, 215, "Antique White"],
  [255, 250, 240, "Ivory"],
]

/**
 * Parse a hex color string to RGB values
 */
function hexToRgb(hex: string): [number, number, number] {
  const clean = hex.replace('#', '')
  const r = parseInt(clean.substring(0, 2), 16)
  const g = parseInt(clean.substring(2, 4), 16)
  const b = parseInt(clean.substring(4, 6), 16)
  return [r, g, b]
}

/**
 * Calculate Euclidean distance between two RGB colors
 */
function colorDistance(rgb1: [number, number, number], rgb2: [number, number, number]): number {
  const dr = rgb1[0] - rgb2[0]
  const dg = rgb1[1] - rgb2[1]
  const db = rgb1[2] - rgb2[2]
  return Math.sqrt(dr * dr + dg * dg + db * db)
}

/**
 * Get a human-readable color name for a hex code.
 * Returns the closest matching CSS color name.
 */
function getColorName(hex: string): string {
  const rgb = hexToRgb(hex)
  let closestName = "Unknown"
  let closestDistance = Infinity

  for (const [r, g, b, name] of CSS_COLORS) {
    const distance = colorDistance(rgb, [r, g, b])
    if (distance < closestDistance) {
      closestDistance = distance
      closestName = name
    }
    if (distance === 0) break
  }

  return closestName
}

/**
 * Format a list of hex colors as "Name - #HEX" strings, one per line.
 */
export function formatColorsForCopy(colors: string[]): string {
  return colors
    .map((hex) => {
      const name = getColorName(hex)
      return `${name} - ${hex.toUpperCase()}`
    })
    .join('\n')
}

/**
 * Convert hex color to HSL values
 */
export function hexToHsl(hex: string): [number, number, number] {
  const [r, g, b] = hexToRgb(hex).map(v => v / 255)
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2

  if (max === min) return [0, 0, l]

  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)

  let h = 0
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6
  else if (max === g) h = ((b - r) / d + 2) / 6
  else h = ((r - g) / d + 4) / 6

  return [h * 360, s, l]
}


